import React from 'react';
import { MessageSquare } from 'lucide-react';

/**
 * SMS Consent Checkbox Component
 * 
 * TCPA-compliant consent statement for SMS communications.
 * Use this component in any form that collects phone numbers.
 */
const SMSConsentCheckbox = ({ 
  checked, 
  onChange, 
  companyName = "Triple R Exteriors",
  required = true,
  variant = "default" // "default" | "compact" | "inline"
}) => {
  const consentText = {
    default: `By checking this box, I consent to receive automated text messages from ${companyName} at the phone number provided above. Messages may include appointment reminders, estimates, follow-ups, and promotional offers. Message frequency varies. Message & data rates may apply. Reply STOP to opt-out at any time. Reply HELP for assistance. View our Privacy Policy and Terms of Service for more information.`,
    compact: `I agree to receive SMS messages from ${companyName}. Msg & data rates may apply. Reply STOP to opt-out.`,
    inline: `I consent to receive text messages from ${companyName}. Msg frequency varies. Msg & data rates may apply. Reply STOP to cancel.`
  };

  if (variant === "inline") {
    return (
      <label className="flex items-start gap-2 text-xs text-slate-500 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          required={required}
          className="mt-0.5 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
        />
        <span>{consentText.inline}</span>
      </label>
    );
  }

  return (
    <div className={`${variant === "compact" ? "p-3" : "p-4"} bg-slate-50 rounded-lg border border-slate-200`}>
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          required={required}
          className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
        />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <MessageSquare className="w-4 h-4 text-orange-500" />
            <span className={`font-medium text-slate-700 ${variant === "compact" ? "text-sm" : ""}`}>
              SMS Communication Consent {required && <span className="text-red-500">*</span>}
            </span>
          </div>
          <p className={`text-slate-600 leading-relaxed ${variant === "compact" ? "text-xs" : "text-sm"}`}>
            {variant === "compact" ? consentText.compact : consentText.default}
          </p>
        </div>
      </label>
    </div>
  );
};

/**
 * SMS Consent Text Only (no checkbox)
 * 
 * Use this for displaying consent language without requiring interaction.
 * Good for confirmation pages or terms display.
 */
export const SMSConsentText = ({ companyName = "Triple R Exteriors" }) => (
  <div className="text-xs text-slate-500 mt-2">
    <p>
      By providing your phone number, you consent to receive automated text messages from {companyName}. 
      Message frequency varies. Message & data rates may apply. 
      Reply STOP to opt-out at any time. Reply HELP for assistance.
    </p>
  </div>
);

/**
 * Full SMS Terms Modal Content
 * 
 * Detailed terms for SMS communications - link to this from forms.
 */
export const SMSTermsContent = ({ companyName = "Triple R Exteriors" }) => (
  <div className="prose prose-sm max-w-none">
    <h3>SMS/Text Message Terms</h3>
    
    <h4>Consent to Receive Text Messages</h4>
    <p>
      By providing your mobile phone number and checking the SMS consent box, you expressly consent 
      to receive automated text messages from {companyName} and its affiliates at the phone number 
      you provided. These messages may include:
    </p>
    <ul>
      <li>Appointment reminders and confirmations</li>
      <li>Roof estimates and project updates</li>
      <li>Follow-up communications regarding your inquiry</li>
      <li>Promotional offers and special discounts</li>
      <li>Important service notifications</li>
    </ul>

    <h4>Message Frequency</h4>
    <p>
      Message frequency varies based on your interactions with us. You may receive multiple 
      messages per week during active projects or inquiries.
    </p>

    <h4>Costs</h4>
    <p>
      Message and data rates may apply depending on your mobile carrier and plan. 
      {companyName} does not charge for text messages, but your carrier may.
    </p>

    <h4>Opt-Out</h4>
    <p>
      You can opt-out of receiving text messages at any time by:
    </p>
    <ul>
      <li>Replying <strong>STOP</strong> to any message</li>
      <li>Contacting us at support@{companyName.toLowerCase().replace(/\s+/g, '')}.com</li>
      <li>Calling our office</li>
    </ul>
    <p>
      After opting out, you will receive one final confirmation message. You will no longer 
      receive text messages from us unless you opt back in.
    </p>

    <h4>Help</h4>
    <p>
      For assistance, reply <strong>HELP</strong> to any message or contact our support team.
    </p>

    <h4>Privacy</h4>
    <p>
      Your phone number and information will be handled in accordance with our Privacy Policy. 
      We do not sell or share your phone number with third parties for marketing purposes.
    </p>

    <h4>Carriers</h4>
    <p>
      Supported carriers include AT&T, Verizon, T-Mobile, Sprint, and most other major carriers. 
      Some carriers may not support all message types.
    </p>
  </div>
);

export default SMSConsentCheckbox;
